import React from 'react';
import { CheckCircle2, AlertTriangle, Clock, XCircle, Mail, FileText, Send } from 'lucide-react';

/**
 * Badge unificato per lo stato SDI di una fattura.
 *
 * Accetta in input sia `ei_status` (codici FattureInCloud) sia `sdi_stato`
 * (codici locali generati dal nostro backend). Restituisce sempre lo stesso
 * badge colorato con icona.
 *
 * Props:
 *  - ei: ei_status FIC (es. 'accepted', 'processing', 'rejected', ...)
 *  - local: sdi_stato locale (es. 'generata', 'consegnata', 'scartata', ...)
 *  - size: 'sm' | 'md' (default sm)
 */
const STATES = {
  // chiave canonica → { label, classi, icona }
  consegnata:   { label: 'Consegnata',  cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  in_transito:  { label: 'In transito', cls: 'bg-amber-100 text-amber-700 border-amber-200',    icon: Clock },
  in_coda:      { label: 'In coda',     cls: 'bg-sky-100 text-sky-700 border-sky-200',           icon: Clock },
  in_invio:     { label: 'In invio',    cls: 'bg-sky-100 text-sky-700 border-sky-200',           icon: Send },
  scartata:     { label: 'Scartata',    cls: 'bg-rose-100 text-rose-700 border-rose-200',        icon: XCircle },
  errore:       { label: 'Errore SDI',  cls: 'bg-rose-100 text-rose-700 border-rose-200',        icon: AlertTriangle },
  mancata:      { label: 'Mancata cons.', cls: 'bg-rose-100 text-rose-700 border-rose-200',      icon: AlertTriangle },
  generata:     { label: 'Generata',    cls: 'bg-indigo-100 text-indigo-700 border-indigo-200',  icon: FileText },
  non_inviata:  { label: 'Non inviata', cls: 'bg-slate-100 text-slate-700 border-slate-200',     icon: Mail },
  non_generata: { label: 'Solo locale', cls: 'bg-slate-100 text-slate-600 border-slate-200',     icon: FileText },
};

// FattureInCloud → canonico
const EI_MAP = {
  not_sent: 'non_inviata',
  pending: 'in_coda',
  processing: 'in_transito',
  accepted: 'consegnata',
  rejected: 'scartata',
  error: 'errore',
  not_received: 'mancata',
};

// Stato locale → canonico (alcuni nomi coincidono)
const LOCAL_MAP = {
  generata: 'generata',
  consegnata: 'consegnata',
  in_transito: 'in_transito',
  in_invio: 'in_invio',
  scartata: 'scartata',
  mancata_consegna: 'mancata',
  non_generata: 'non_generata',
};

export default function SdiBadge({ ei, local, size = 'sm', message }) {
  // EI ha priorità (più aggiornato dal webhook)
  let key = null;
  if (ei) key = EI_MAP[ei.toLowerCase()] || null;
  if (!key && local) key = LOCAL_MAP[local.toLowerCase()] || null;
  if (!key) key = 'non_generata';

  const s = STATES[key] || STATES.non_generata;
  const Icon = s.icon;
  const dims = size === 'md'
    ? 'text-xs px-2.5 py-1 gap-1.5'
    : 'text-[10px] px-1.5 py-0.5 gap-1';
  return (
    <span
      title={message || s.label}
      data-testid={`sdi-badge-${key}`}
      className={`inline-flex items-center font-bold border rounded ${s.cls} ${dims}`}
    >
      <Icon size={size === 'md' ? 12 : 10} /> {s.label}
    </span>
  );
}
